import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import type { UserPermission } from '@adornis/users/db/types.js';

/**
 * The role a user holds *within one organization*.
 *
 * Deliberately not the same axis as platform admin, which is a property of the
 * account rather than of a membership and therefore lives in `isPlatformAdmin`.
 * Modelling it as a third value here would mean a platform admin had to belong
 * somewhere to be one.
 */
export enum SaraaOrgRole {
  /** Manages the organization: its templates, template fields and members. */
  ORG_ADMIN = 'ORG_ADMIN',
  /** Uses the generate page with the organization's templates. */
  ORG_USER = 'ORG_USER',
}

registerEnum({ name: 'SaraaOrgRole', enum: SaraaOrgRole });

/**
 * A user's membership in one organization, together with the role they hold
 * there. Embedded on `SaraaUser` rather than kept in its own collection so the
 * client's `fetchUser` stays the already-live `currentUser()` subscription — the
 * ERM issues many ABAC evaluations per page render and each one calls it.
 *
 * `_id` follows the `PermissionGrant` pattern: stable ids matter because
 * `Change.getDiff` is array-length sensitive and forms behave far better with
 * them.
 */
@Entity()
export class OrganizationMembership extends AdornisEntity {
  static override _class = 'OrganizationMembership';

  constructor(doc: PartialEntityData<OrganizationMembership>) {
    super(doc);
  }

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @Field(() => ID)
  organizationId: Maybe<string>;

  @Field(() => SaraaOrgRole)
  role: Maybe<SaraaOrgRole>;
}

/**
 * Saraa's user class.
 *
 * Overrides only `_class`, so it shares `AdornisUser`'s `adornisuser` collection
 * and existing documents keep working — they only need their `_class` migrated
 * (see `migrateUsersToSaraaUser`). A document left at `_class: 'AdornisUser'`
 * resolves to a class without `memberships` and is silently treated as belonging
 * to no organization.
 */
@Entity()
export class SaraaUser<Permission extends string = UserPermission> extends AdornisUser<Permission> {
  static override _class = 'SaraaUser';
  static override _collectionName = AdornisUser._collectionName;

  constructor(doc: PartialEntityData<SaraaUser>) {
    super(doc);
  }

  /**
   * Which organizations this user belongs to, and as what.
   *
   * Read through a resolver so **every** GraphQL read is scoped to what the
   * caller may see — the dedicated membership query, a hand-written `getByID`,
   * or the `allFields` selection `subscribeCurrentUser` uses. Filtering only the
   * dedicated query would have left the field readable directly, and which
   * organizations a user belongs to is information about those organizations.
   *
   * Being a resolved field also means `toJSON()` omits it (`toObject(true)`
   * drops resolved fields), so it cannot ride along in a document write at all —
   * a second lock on top of its absence from `SARAA_USER_EDITABLE_FIELDS`. The
   * corollary is that it is not persisted on creation either: creation flows must
   * write the membership explicitly afterwards, via `writeMembership`.
   *
   * Server-side authorization never reads this; it uses `readStoredMemberships`,
   * which returns the unfiltered truth.
   */
  @Field(() => [OrganizationMembership], {
    default: v => v ?? [],
    resolve: function (this: SaraaUser) {
      return async (): Promise<OrganizationMembership[]> => {
        if (!A.isServer) return [];
        const { resolveVisibleMembershipsFor } = await import('../server/membershipVisibility.js');
        const visible = await resolveVisibleMembershipsFor(this._id);
        return visible.map(membership => new OrganizationMembership(membership));
      };
    },
  })
  memberships!: OrganizationMembership[];

  /**
   * Whether this user is a platform administrator.
   *
   * The single variable input to grant resolution: `resolveGrantsFor` reads it and
   * nothing else about the user. Everything else a user may do is decided by
   * `memberships` through the scope checks.
   *
   * Resolved rather than plain, for the same structural reason as `memberships`.
   * `toObject(true)` drops resolved fields, so it cannot ride along in a document
   * write at all — a second lock on top of its absence from
   * `SARAA_USER_EDITABLE_FIELDS`, which is what actually governs `updateUser`.
   * Worth both locks: this is the one field that, if writable by its owner, would
   * let any user promote themselves.
   *
   * It moves exclusively through `setPlatformAdmin`, behind `PlatformAdminAPI`.
   * Server-side authorization never reads this resolver — `fetchUser` and
   * `loadPermissionUser` project the stored value directly.
   *
   * Not filtered per caller the way memberships are: `UserAPI` already decides
   * which users you can read at all, and among those, who administers the platform
   * is not a secret worth a second mechanism.
   */
  @Field(() => Boolean, {
    default: v => v ?? false,
    resolve: function (this: SaraaUser) {
      return async (): Promise<boolean> => {
        if (!A.isServer) return false;
        try {
          const { readStoredPlatformAdmin } = await import('../server/platformAdmin.js');
          return await readStoredPlatformAdmin(this._id);
        } catch (error) {
          /**
           * Fails closed *here*, and only here. `subscribeCurrentUser` selects
           * every field, so this runs on the login path and a throw would take
           * sign-in down for everybody; reading as "not an administrator" is
           * visible and recoverable.
           *
           * The reader itself deliberately throws, because the same value is an
           * authorization input elsewhere — and there, answering `false` on a
           * failed read is the wrong direction: `isLastPlatformAdmin` would take
           * it as "not the last one" and allow the deletion. Display degrades;
           * decisions refuse.
           */
          logger.error({ err: error, userId: this._id }, '[saraa/permissions] could not read platform admin status');
          return false;
        }
      };
    },
  })
  isPlatformAdmin!: boolean;
}

/**
 * The only fields `updateUser` takes from the caller.
 *
 * Everything else about an account moves through the endpoint that owns it:
 * `memberships` through the membership mutations, `archivedAt` through archive,
 * the password through the login flows, `roles` not at all. Keeping the list
 * this short is what makes the user form safe to autosave.
 */
export const SARAA_USER_EDITABLE_FIELDS = ['username', 'email'] as const satisfies ReadonlyArray<keyof SaraaUser>;

/**
 * Structural view of a membership.
 *
 * The helpers below are typed against this rather than the entity class because
 * they run over both hydrated entities (client) and raw Mongo documents
 * (server), and a raw document is not an `OrganizationMembership` instance.
 */
export interface MembershipLike {
  organizationId?: Maybe<string>;
  role?: Maybe<SaraaOrgRole>;
}

/** Structural view of a user, as far as any permission check is concerned. */
export interface UserWithMemberships {
  _id: string;
  memberships?: Maybe<MembershipLike[]>;
  /**
   * Absent means "not an administrator", never "unknown". The one place that
   * distinction matters is the backfill, which asks the database directly.
   */
  isPlatformAdmin?: Maybe<boolean>;
}

/** The organization ids where this user holds the given role. */
export const organizationIdsWithRole = (user: Maybe<UserWithMemberships>, role: SaraaOrgRole): string[] =>
  (user?.memberships ?? []).flatMap(membership =>
    membership.role === role && membership.organizationId ? [membership.organizationId] : [],
  );

/** Every organization id this user belongs to, whatever the role. */
export const organizationIdsOfUser = (user: Maybe<UserWithMemberships>): string[] =>
  (user?.memberships ?? []).flatMap(membership => (membership.organizationId ? [membership.organizationId] : []));

export const isOrgAdminOfOrganization = (user: Maybe<UserWithMemberships>, organizationId: Maybe<string>) =>
  !!organizationId && organizationIdsWithRole(user, SaraaOrgRole.ORG_ADMIN).includes(organizationId);

export const isMemberOfOrganization = (user: Maybe<UserWithMemberships>, organizationId: Maybe<string>) =>
  !!organizationId && organizationIdsOfUser(user).includes(organizationId);

/** True when the user administers at least one organization. */
export const isOrgAdminAnywhere = (user: Maybe<UserWithMemberships>) =>
  organizationIdsWithRole(user, SaraaOrgRole.ORG_ADMIN).length > 0;
